import { useState } from 'react';
import { useWithdrawApplication } from '../../hooks/unified/useApplicationsQueries';
import { useToast } from '../../contexts/ToastContext';
import type { ApplicationWithDetails } from '../../services/unified/types';

interface ApplicationCardProps {
  application: ApplicationWithDetails;
  onUpdate?: () => void;
}

export function ApplicationCard({ application, onUpdate }: ApplicationCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const withdrawMutation = useWithdrawApplication();
  const { showSuccess, showError } = useToast();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'interview': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'offer': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'withdrawn': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'submitted':
        return (
          <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'reviewing':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        );
      case 'interview':
        return (
          <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'offer':
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'rejected':
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'withdrawn':
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getNextSteps = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'Your application is being processed. You should hear back within 1-2 weeks.';
      case 'reviewing':
        return 'Your application is under review. This typically takes 3-5 business days.';
      case 'interview':
        return 'Prepare for your interview by researching the company and practicing common questions.';
      case 'offer':
        return 'Review the offer terms carefully and respond within the specified timeframe.';
      case 'rejected':
        return 'Keep applying to other opportunities and consider asking for feedback.';
      case 'withdrawn':
        return 'You have withdrawn your application for this position.';
      default:
        return 'Status update pending.';
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            {/* Company Logo or Status Icon */}
            <div className="flex-shrink-0">
              {application.company_logo_url ? (
                <img
                  src={application.company_logo_url}
                  alt={application.job_company || 'Company'}
                  className="h-12 w-12 rounded-lg object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={application.company_logo_url ? 'hidden' : ''}>
                {getStatusIcon(application.status)}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {application.job_title || 'Job Title'}
              </h3>
              <div className="flex items-center space-x-2 mb-2">
                <p className="text-md text-gray-600">{application.job_company || 'Company'}</p>
                {application.job_location && (
                  <>
                    <span className="text-gray-400">•</span>
                    <p className="text-sm text-gray-500">{application.job_location}</p>
                  </>
                )}
              </div>
              {application.job_salary_min && application.job_salary_max && (
                <p className="text-sm text-gray-600 mb-2">
                  INR {application.job_salary_min.toLocaleString()} - {application.job_salary_max.toLocaleString()}
                </p>
              )}
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>Applied {formatDate(application.applied_at || '')}</span>
                {application.updated_at && application.updated_at !== application.applied_at && (
                  <span>Updated {formatDate(application.updated_at)}</span>
                )}
                {application.resume_filename && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="flex items-center">
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {application.resume_label || application.resume_filename}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.status)}`}>
              {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
            </span>
          </div>
        </div>

        {/* Job Image */}
        {application.job_image_url && (
          <div className="mt-4">
            <img
              src={application.job_image_url}
              alt={application.job_title || 'Job'}
              className="w-full h-48 object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {getNextSteps(application.status)}
          </div>
          <div className="flex items-center space-x-3">
            {application.status !== 'withdrawn' && application.status !== 'rejected' && (
              <button
                onClick={() => setShowWithdrawConfirm(true)}
                disabled={withdrawMutation.isPending}
                className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
              >
                Withdraw
              </button>
            )}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {showDetails ? 'Hide Details' : 'View Details'}
            </button>
          </div>
        </div>

        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-900">Application ID:</span>
                <span className="ml-2 text-gray-600 font-mono">{application.id}</span>
              </div>
              <div>
                <span className="font-medium text-gray-900">Job ID:</span>
                <span className="ml-2 text-gray-600 font-mono">{application.job_id}</span>
              </div>
              <div>
                <span className="font-medium text-gray-900">Submitted:</span>
                <span className="ml-2 text-gray-600">{formatDateTime(application.applied_at || '')}</span>
              </div>
              <div>
                <span className="font-medium text-gray-900">Last Updated:</span>
                <span className="ml-2 text-gray-600">{formatDateTime(application.updated_at || '')}</span>
              </div>
            </div>

            {application.status === 'interview' && (
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Interview Preparation Tips</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Research the company's mission, values, and recent news</li>
                  <li>• Prepare examples that demonstrate your relevant skills</li>
                  <li>• Practice common interview questions</li>
                  <li>• Prepare thoughtful questions about the role and company</li>
                  <li>• Test your technology if it's a virtual interview</li>
                </ul>
              </div>
            )}

            {application.status === 'offer' && (
              <div className="mt-4 p-3 bg-green-50 rounded-md">
                <h4 className="text-sm font-medium text-green-900 mb-2">Offer Evaluation Checklist</h4>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Review salary and benefits package</li>
                  <li>• Consider work-life balance and company culture</li>
                  <li>• Evaluate growth opportunities and career path</li>
                  <li>• Negotiate if appropriate</li>
                  <li>• Respond within the specified timeframe</li>
                </ul>
              </div>
            )}

            <div className="mt-4 flex space-x-3">
              <a
                href={`/jobs/${application.job_id}`}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View Job Details
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Withdraw Confirmation Modal */}
      {showWithdrawConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Withdraw Application</h3>
                <p className="text-sm text-gray-600 mb-1">
                  Are you sure you want to withdraw your application for <span className="font-medium">{application.job_title}</span>?
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  This action cannot be undone. You will need to reapply if you change your mind.
                </p>
                <div className="flex space-x-3 justify-end">
                  <button
                    onClick={() => setShowWithdrawConfirm(false)}
                    disabled={withdrawMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await withdrawMutation.mutateAsync(application.id);
                        showSuccess('Application withdrawn successfully');
                        setShowWithdrawConfirm(false);
                        onUpdate?.();
                      } catch (error) {
                        showError('Failed to withdraw application');
                      }
                    }}
                    disabled={withdrawMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                  >
                    {withdrawMutation.isPending ? 'Withdrawing...' : 'Withdraw Application'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}